import React from 'react'
import { useTranslation } from 'react-i18next'
import { InstructionsPure } from '../../pures'
import './FileUploadMessage.css'

/**
 * @author Edgar Escobedo
 * @description FileUploadMessage - Reusable component to display the file upload message with instructions
 * @typedef {Object} Props
 * @property {string} [uploadFilesIcon] - URL of the upload icon. If not provided, the default icon is used.
 * @property {Array<React.ReactNode>} [instructions] - Array of custom instructions. If not provided, default instructions are used.
 * @property {string} [uploadText] - Custom text for the upload button. Default is "Drag or upload file".
 * @property {function} onChange - Function that is executed when a file is selected.
 * @property {boolean} [multiple] - Whether multiple files can be selected. Default is false.
 * @property {string} [acceptFiles] - Accepted file types. Default accepts PDF, images, audio, and video.
 * @property {boolean} [hasError] - If there is an error, displays the text in red. Default is false.
 * @property {string} [inputId] - Custom ID for the file input. Default is 'file-upload-message'.
 * @property {boolean} [enableDragAndDrop] - Whether drag and drop is enabled. Default is true.
 * @returns {JSX.Element} The file upload message component
 */
const FileUploadMessage = ({
  uploadFilesIcon,
  instructions,
  uploadText,
  onChange,
  multiple = false,
  acceptFiles = 'application/pdf,image/jpeg,image/png,audio/mpeg,video/mp4',
  hasError = false,
  inputId = 'file-upload-message',
  enableDragAndDrop = true
}) => {
  const { t } = useTranslation()
  const [isDragging, setIsDragging] = React.useState(false)

  const defaultUploadIcon = `${process.env.REACT_APP_AWS_S3_BUCKET_URL}${process.env.REACT_APP_UPLOAD_FILES_ICON}`
  const iconUrl = uploadFilesIcon || defaultUploadIcon

  const defaultUploadText = t('digitalVaultUploadView.upload.file')

  const handleDragOver = (e) => {
    if (!enableDragAndDrop) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    if (!enableDragAndDrop) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    if (!enableDragAndDrop) return
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      // Crear un evento sintético para mantener la misma interfaz que onChange
      const syntheticEvent = {
        target: {
          files: files
        }
      }
      onChange(syntheticEvent)
    }
  }

  return (
    <div
      className={`fileUploadMessage-container ${isDragging ? 'fileUploadMessage-dragging' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className='fileUploadMessage-upload'>
        <div className='fileUploadMessage-upload-section'>
          {instructions && instructions.length > 0 && (
            <div className='fileUploadMessage-instructions'>
              <InstructionsPure instructions={instructions} />
            </div>
          )}
          <div className='fileUploadMessage-upload-icon'>
            <label htmlFor={inputId} className='fileUploadMessage-file-upload'>
              <input
                type='file'
                id={inputId}
                accept={acceptFiles}
                multiple={multiple}
                onClick={(e) => (e.target.value = null)}
                onChange={onChange}
              />
              <div className='fileUploadMessage-icon-box'>
                <img src={iconUrl} alt="upload files" style={{ width: '60px', height: '60px' }} />
                <span style={hasError ? { color: 'red' } : null}>
                  {uploadText || defaultUploadText}
                </span>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FileUploadMessage
